/**
 * Slash command executor — pure logic.
 *
 * Scope commands (/mine, /all) flip the outline's project scope and dispatch
 * outline-render so the outline sections update in place.  The bar shows a
 * single feedback line summarising the result — it no longer renders an
 * inline results list.
 *
 * Result descriptor shapes returned to CommandBar:
 *   { type: 'help' }                   — bar renders COMMANDS list as suggestions
 *   { type: 'filter',  feedback }      — outline filter was set; show summary
 *   { type: 'info',    feedback }      — info-only, no outline change
 *   { type: 'picker',  items, feedback? } — selectable list (e.g. /go multi-match)
 *   { type: 'navigate' }               — bar closes (navigation already happened)
 *   { type: 'error',   feedback }      — error message
 *   { type: 'empty',   feedback }      — no results found
 *
 * Picker item shape:  { id, label, sublabel? }
 *
 * Commands NEVER read from ctx cache (ctx.state, etc.). They fetch their own data through the
 * service layer — the one exception being the crew index (outline/crew.js), which is itself a
 * service-backed cache shared with every crew chip on the board.
 */

import { wf, saveViewState } from './state.js'
import { isDark, toggleTheme } from './theme.js'
import { isDebug, setDebug, isEligible as isDebugEligible } from './debugOverlay.js'
import { ctx } from './ctx.js'
import { getActiveProjects, createTimeEntry } from '../services/commands.js'
import { ensureCrewIndex, crewForMilestone } from './crew.js'
import { setProjectScope } from './projectScope.js'
import { getBallInCourtReviews } from '../services/permits.js'
import { zoomObjFromWf, buildSplitUrl } from '../components/splitView.js'

/* ── Helpers ────────────────────────────────────────────────────────────────── */

function fmtDate(iso) {
  if (!iso) return ''
  const p = String(iso).split(/[-T]/g)
  if (p.length < 3) return iso
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return months[parseInt(p[1], 10) - 1] + ' ' + parseInt(p[2], 10)
}

function plural(n, word) {
  return n + ' ' + word + (n === 1 ? '' : 's')
}

/* ── Executors ──────────────────────────────────────────────────────────────── */

function execHome(ctx) {
  ctx.closeAllWorkspaces()
  ctx.zoomToProjectList()
  return { type: 'navigate' }
}

function execDark() {
  if (!isDark()) toggleTheme()
  return { type: 'navigate' }
}

function execLight() {
  if (isDark()) toggleTheme()
  return { type: 'navigate' }
}

async function execHelp() {
  return { type: 'help' }
}

/* ── Project scope + crew ─────────────────────────────────────────────────────── */

// /mine and /all flip the outline's project scope (docs/FLOW_RECONCILIATION_PLAN.md, decision 10)
// and go home. A person's own flights are always pinned above the outline (MyFlightsStrip), so
// there is no separate "my work" surface to open any more.
async function execMine(ctx) {
  setProjectScope('mine')
  ctx.closeAllWorkspaces()
  ctx.zoomToProjectList()
  return { type: 'navigate' }
}

async function execAll(ctx) {
  setProjectScope('all')
  ctx.closeAllWorkspaces()
  ctx.zoomToProjectList()
  return { type: 'navigate' }
}

// Who is flying the zoomed milestone. A queued milestone has no crew by design — the tower
// assigns one at launch, on the Flight Board.
async function execTeam() {
  const msId = wf.zoomMsId
  if (!msId) {
    return { type: 'error', feedback: 'Navigate into a milestone first, then run /team.' }
  }
  await ensureCrewIndex()
  const flight = crewForMilestone(msId)
  if (!flight) {
    return { type: 'empty', feedback: 'Not in the air — no crew. Launch and crew it from the Flight Board.' }
  }
  const crew = (flight.crew || []).map(c => c.name + (c.isCaptain ? ' (captain)' : ''))
  return {
    type: 'info',
    feedback: flight.flightNumber + ' · ' + (crew.length ? crew.join(', ') : 'no crew seated yet'),
  }
}

async function execGo(args, ctx) {
  const projects = await getActiveProjects()
  if (!projects.length) {
    return { type: 'empty', feedback: 'No active projects found.' }
  }

  const items = projects.map(p => ({
    id:        p.id,
    label:     (p.fields['Project Number'] ? p.fields['Project Number'] + ' · ' : '') +
               (p.fields['Project Name'] || 'Unnamed'),
    sublabel:  '',
    projectId: p.id,
  }))

  const query = (args || '').trim().toLowerCase()
  if (query) {
    const match = items.find(i => i.label.toLowerCase().includes(query))
    if (match) {
      ctx.closeAllWorkspaces()
      ctx.zoomToProject(match.projectId)
      return { type: 'navigate' }
    }
    const filtered = items.filter(i => i.label.toLowerCase().includes(query))
    if (!filtered.length) {
      return { type: 'empty', feedback: 'No project matching "' + args.trim() + '".' }
    }
    return { type: 'picker', items: filtered, feedback: plural(filtered.length, 'match') }
  }

  return { type: 'picker', items, feedback: plural(items.length, 'active project') }
}

async function execTime(args, ctx) {
  const hoursStr = (args || '').trim().replace(/h$/i, '')
  const hours    = parseFloat(hoursStr)

  if (!hoursStr || isNaN(hours) || hours <= 0) {
    return { type: 'error', feedback: 'Usage: /time [hours] — e.g. /time 2.5' }
  }

  const memberId = wf.currentMemberId
  if (!memberId) {
    return { type: 'error', feedback: 'Not signed in — cannot log time.' }
  }

  const phaseId = wf.zoomPhaseId
  if (!phaseId) {
    return { type: 'error', feedback: 'Navigate to a phase or milestone first, then run /time.' }
  }

  const today = new Date()
  const date  = today.getFullYear() + '-' +
    String(today.getMonth() + 1).padStart(2, '0') + '-' +
    String(today.getDate()).padStart(2, '0')

  const res = await createTimeEntry({ memberId, phaseId, hours, date })
  if (res.error) {
    return { type: 'error', feedback: 'Could not save time entry: ' + res.error }
  }

  return { type: 'info', feedback: hours + 'h logged for today · pending review' }
}

/* ── Expand / collapse helpers ──────────────────────────────────────────────── */

function getZoomKey() {
  if (wf.zoomMsId)      return 'ms:'   + wf.zoomMsId
  if (wf.zoomPhaseId)   return 'ph:'   + wf.zoomPhaseId
  if (wf.zoomRoot)      return 'root:' + wf.zoomRoot
  if (wf.zoomProjectId) return 'proj:' + wf.zoomProjectId
  return 'root:projects'   // Home — expand/collapse every project row
}

function execExpand(ctx) {
  const key = getZoomKey()
  if (!key) return { type: 'info', feedback: 'Open a project first — /expand works within a project.' }
  ctx.closeAllWorkspaces()
  ctx.expandCollapseDescendants(key, true)
  return { type: 'navigate' }
}

function execContract(ctx) {
  const key = getZoomKey()
  if (!key) return { type: 'info', feedback: 'Open a project first — /collapse works within a project.' }
  ctx.closeAllWorkspaces()
  ctx.expandCollapseDescendants(key, false)
  return { type: 'navigate' }
}

function execFilter() {
  const isHidden = !!(wf.hideDoneKeys?.global)
  const hideItem = {
    id:       'filter-hide',
    label:    'Hide Complete',
    sublabel: 'hides posted and done milestones across all views',
  }
  const showItem = {
    id:       'filter-show',
    label:    'Show Complete',
    sublabel: 'shows all milestones including posted ones',
  }
  return {
    type: 'picker',
    feedback: isHidden
      ? 'Completed milestones are currently hidden'
      : 'Completed milestones are currently visible',
    /* Put the action that CHANGES current state first so Enter always does the expected thing */
    items: isHidden ? [showItem, hideItem] : [hideItem, showItem],
  }
}

/* ── /permits — cross-project triage: agency reviews where we have the ball ─── */
async function execPermits() {
  const reviews = await getBallInCourtReviews()
  if (!reviews.length) {
    return { type: 'empty', feedback: 'No agency reviews where we have the ball.' }
  }

  // Overdue (negative days-until-deadline) first, then soonest deadline, nulls last.
  const sorted = reviews.slice().sort((a, b) => {
    const da = a.daysUntilDeadline, db = b.daysUntilDeadline
    if (da == null && db == null) return 0
    if (da == null) return 1
    if (db == null) return -1
    return da - db
  })

  const items = sorted
    .filter(r => r.projectId)
    .map(r => {
      const overdue = r.daysUntilDeadline != null && r.daysUntilDeadline < 0
      const dl = r.nextDeadline ? (overdue ? 'overdue · ' : 'due ') + fmtDate(r.nextDeadline) : ''
      const proj = r.projectNumber || r.projectName || ''
      return {
        id:        r.id,
        label:     (proj ? proj + ' · ' : '') + (r.agency || r.name || 'Agency Review'),
        sublabel:  [r.status, dl].filter(Boolean).join(' · '),
        projectId: r.projectId,
      }
    })

  return {
    type: 'picker',
    feedback: plural(items.length, 'review') + ' — we have the ball',
    items,
  }
}

/* ── /drawings — jump to Drawing Sheets for the current project ─────────────── */
function execDrawings() {
  if (!wf.zoomProjectId) return { type: 'error', feedback: 'Open a project first, then run /drawings.' }
  ctx.closeAllWorkspaces()
  ctx.zoomToSection('sec:prod:sheets') /* pushes browser history */
  return { type: 'navigate' }
}

/* ── /bim — jump to BIM Systems for the current project ─────────────────────── */
function execBim() {
  if (!wf.zoomProjectId) return { type: 'error', feedback: 'Open a project first, then run /bim.' }
  ctx.closeAllWorkspaces()
  ctx.zoomToSection('sec:design:bim', { root: 'design' }) /* pushes browser history */
  return { type: 'navigate' }
}

/* ── /sketch — open the trace and sketch tool ────────────────────────────────── */
function execSketch() {
  window.location.assign('/sketch')
  return { type: 'navigate' }
}

/* ── /cad — open a project-less AlderCAD sandbox model (ModelerPage ?sandbox=).
   Bare /cad opens the shared 'scratch' sandbox; /cad <name> opens (or creates) its own. ─ */
function execCad(args) {
  const name = (args || '').trim() || 'scratch'
  window.open('/modeler?sandbox=' + encodeURIComponent(name), '_blank')
  return { type: 'navigate' }
}

/* ── /aj — secret: AJ the Coastal Fox walks across the bottom of the screen.
   `/aj debug` opens a centered, orbitable inspect view (drag to rotate) instead. ─ */
function execAj(args) {
  const debug = /debug/i.test(args || '')
  window.dispatchEvent(new CustomEvent('aj-walk', { detail: { debug } }))
  return { type: 'navigate' }
}

/* ── /refresh — clear all caches, remount lazy-loaded sections, refetch current view ── */
function execRefresh(ctx) {
  if (ctx.refreshAll) ctx.refreshAll()
  return { type: 'info', feedback: 'Refreshing…' }
}

/* ── /debug — toggle node-ID badges over every bullet (manager-and-up only) ───── */
function execDebug() {
  if (!isDebugEligible()) {
    return { type: 'error', feedback: 'Debug mode is available to managers and up.' }
  }
  const next = !isDebug()
  setDebug(next)
  return {
    type: 'info',
    feedback: next
      ? 'Debug node IDs ON — each bullet shows its node key. Run /debug again to hide.'
      : 'Debug node IDs OFF.',
  }
}

/* ── /split — open a side-by-side replica of the current screen (same mechanism as Alt+click on
   a node, CT-024) but callable from anywhere, not just from a clicked node. Reads live wf zoom
   state directly (zoomObjFromWf) rather than reconstructing from DOM attributes, then carries
   over any open full-bleed workspace (Billing/CRM/Pulse/Team/Tracker's ?ws=/&wsTool=/&wsRunId=)
   so the split pane replicates that too, not just the outline underneath it. ── */
function execSplit(ctx) {
  const projFallback = wf.zoomProjectId || ctx.recordId || null
  let url = buildSplitUrl(window.location.origin, zoomObjFromWf(wf, projFallback))
  const cur = new URLSearchParams(window.location.search)
  const carry = ['ws', 'wsTool', 'wsRunId']
    .map(k => [k, cur.get(k)])
    .filter(([, v]) => v)
    .map(([k, v]) => k + '=' + encodeURIComponent(v))
  if (carry.length) url += '&' + carry.join('&')
  window.dispatchEvent(new CustomEvent('open-split-pane', { detail: { url, title: document.title || 'Split view' } }))
  return { type: 'navigate' }
}

/* ── Main entry points ──────────────────────────────────────────────────────── */

/**
 * Execute a command and return a result descriptor.
 * @param {string} cmdId
 * @param {string} args — text after the trigger (trimmed)
 * @param {object} ctx  — the ctx singleton from ctx.js
 */
export async function executeCommand(cmdId, args, ctx) {
  switch (cmdId) {
    case 'home':     return execHome(ctx)
    case 'dark':     return execDark()
    case 'light':    return execLight()
    case 'help':     return execHelp()
    case 'mine':     return execMine(ctx)
    case 'all':      return execAll(ctx)
    case 'team':     return execTeam()
    case 'permits':  return execPermits()
    case 'go':       return execGo(args, ctx)
    case 'time':     return execTime(args, ctx)
    case 'expand':   return execExpand(ctx)
    case 'collapse': return execContract(ctx)
    case 'filter':   return execFilter()
    case 'drawings': return execDrawings()
    case 'bim':      return execBim()
    case 'sketch':   return execSketch()
    case 'cad':      return execCad(args)
    case 'aj':       return execAj(args)
    case 'debug':    return execDebug()
    case 'refresh':  return execRefresh(ctx)
    case 'split':    return execSplit(ctx)
    default:         return { type: 'error', feedback: 'Unknown command: ' + cmdId }
  }
}

/**
 * Called when the user selects an item from a picker result.
 * Returns a result descriptor (often { type: 'navigate' } to close the bar).
 * @param {string} cmdId
 * @param {object} item — the selected picker item
 * @param {object} ctx
 */
export function selectPickerItem(cmdId, item, ctx) {
  if (cmdId === 'go') {
    ctx.closeAllWorkspaces()
    ctx.zoomToProject(item.projectId)
    return { type: 'navigate' }
  }

  // Permit triage — open the project, then focus its Permits root.
  if (cmdId === 'permits') {
    ctx.closeAllWorkspaces()
    ctx.zoomToProject(item.projectId)
    if (ctx.zoomToRoot) {
      // Defer so the project load settles before switching to the Permits root.
      setTimeout(() => ctx.zoomToRoot('permits'), 0)
    }
    return { type: 'navigate' }
  }

  // Filter: show / hide complete
  if (cmdId === 'filter') {
    if (item.id === 'filter-hide') {
      ctx.closeAllWorkspaces()
      wf.hideDoneKeys = { ...wf.hideDoneKeys, global: true }
      saveViewState()
      window.dispatchEvent(new Event('outline-render'))
      return { type: 'navigate' }
    }
    if (item.id === 'filter-show') {
      ctx.closeAllWorkspaces()
      const copy = { ...wf.hideDoneKeys }
      delete copy.global
      wf.hideDoneKeys = copy
      saveViewState()
      window.dispatchEvent(new Event('outline-render'))
      return { type: 'navigate' }
    }
  }

  return { type: 'error', feedback: 'Cannot select from this result.' }
}
